import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch } from "../utils/authFetch";
import { useProject } from "../contexts/ProjectContext";

export interface MergeRequest {
  id: string;
  projectId: string;
  sourceBranchId: string;
  targetBranchId: string;
  requestedBy: string;
  status: "pending" | "rejected" | "merged" | "outdated" | "closed";
  /** Why an "outdated" request went outdated (see utils/mergeRequestStatus). */
  statusReason?: string | null;
  strategy: "merge" | "overwrite";
  approvedBy: string | null;
  approvedAt: string | null;
  mergedAt: string | null;
  sourceHeadAtCreation: string | null;
  targetHeadAtCreation: string | null;
  sourceSequence: number | null;
  targetSequence: number | null;
  createdAt: string;
  /** "Keep source branch unchanged": the rebased copy of the source branch
   *  this request merges (the branch itself is left as it is). */
  mergeHeadCommitId?: string | null;
  // Joined fields from API
  sourceBranchName?: string;
  targetBranchName?: string;
  requestedByName?: string;
  approvedByName?: string | null;
  diffSummary?: {
    added: { slugPath: string; title: string }[];
    modified: { slugPath: string; title: string }[];
    deleted: { slugPath: string; title: string }[];
    addedCount: number;
    modifiedCount: number;
    deletedCount: number;
    totalChanges: number;
  };
}

interface MergeRequestFilters {
  status?: string;
  targetBranch?: string;
}

/**
 * Fetch a list of merge requests for the current project with optional filters.
 * Validates: Requirements 26.10, 26.7
 */
export function useMergeRequests(filters?: MergeRequestFilters) {
  const { currentProject } = useProject();
  const slug = currentProject?.slug;

  const [mergeRequests, setMergeRequests] = useState<MergeRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only the latest refetch may write state: the unfiltered list is slower than a
  // filtered one, so an older filter's response would otherwise land last and win.
  const seq = useRef(0);

  const refetch = useCallback(async () => {
    const my = ++seq.current;
    if (!slug) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.set("status", filters.status);
      if (filters?.targetBranch) params.set("target_branch", filters.targetBranch);
      const qs = params.toString();
      const url = `/api/projects/${slug}/studio/merge-requests${qs ? `?${qs}` : ""}`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`Failed to fetch merge requests: ${res.statusText}`);
      const data: MergeRequest[] = await res.json();
      if (my === seq.current) setMergeRequests(data);
    } catch (err) {
      if (my === seq.current) setError(String(err));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [slug, filters?.status, filters?.targetBranch]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Listen for preview-reload events from CLI commands
  useEffect(() => {
    const handler = () => { refetch(); };
    window.addEventListener("preview-reload", handler);
    return () => window.removeEventListener("preview-reload", handler);
  }, [refetch]);

  return { mergeRequests, loading, error, refetch };
}

/**
 * Fetch a single merge request detail (including diff summary).
 */
export function useMergeRequestDetail(mrId: string) {
  const { currentProject } = useProject();
  const slug = currentProject?.slug;

  const [mergeRequest, setMergeRequest] = useState<MergeRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || !mrId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await authFetch(`/api/projects/${slug}/studio/merge-requests/${mrId}`);
        if (cancelled) return;
        if (!res.ok) throw new Error(`Failed to fetch merge request: ${res.statusText}`);
        const data: MergeRequest = await res.json();
        if (!cancelled) setMergeRequest(data);
      } catch (err) {
        if (!cancelled) setError(String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, mrId]);

  return { mergeRequest, loading, error };
}

/**
 * Approve a merge request.
 */
export function useApproveMergeRequest() {
  const { currentProject } = useProject();
  const slug = currentProject?.slug;
  const [loading, setLoading] = useState(false);

  const approve = useCallback(async (mrId: string) => {
    if (!slug) throw new Error("No project selected");
    setLoading(true);
    try {
      const res = await authFetch(`/api/projects/${slug}/studio/merge-requests/${mrId}/approve`, {
        method: "POST",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to approve merge request: ${res.statusText}`);
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  return { approve, loading };
}

/**
 * Reject a merge request with an optional reason.
 */
export function useRejectMergeRequest() {
  const { currentProject } = useProject();
  const slug = currentProject?.slug;
  const [loading, setLoading] = useState(false);

  const reject = useCallback(async (mrId: string, reason?: string) => {
    if (!slug) throw new Error("No project selected");
    setLoading(true);
    try {
      const res = await authFetch(`/api/projects/${slug}/studio/merge-requests/${mrId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to reject merge request: ${res.statusText}`);
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  return { reject, loading };
}
